import React, { FunctionComponent, ReactNode, useState } from 'react';
import Downshift, { DownshiftProps, StateChangeOptions } from 'downshift';
import {
	getItemDisplayValueDefault,
	getItemKeyDefault,
	IDropdownItem,
} from '../../DropdownList';
import { MultiSelectRoot } from './MultiSelectRoot';

export interface IMultiSelectProps<T = IDropdownItem>
	extends Omit<DownshiftProps<T>, 'onChange'> {
	items: T[];
	onChange: (selectedItems: T[]) => void;
	initialSelectedItems?: T[];
	itemToKey?: (item: T) => string;
	itemToString?: (item: T | null) => string;
	dropdownItemValue?: (item: T | null) => string | React.JSX.Element;
	placeholder?: string;
	ariaLabel?: string;
	disabled?: boolean;
	invalid?: boolean;
	className?: string;
	icon?: ReactNode;
	placeholderAsLabel?: boolean;
	onClick?: () => void;
}

export interface ISelectComponent<Props = IMultiSelectProps>
	extends FunctionComponent<Props> {}

export function MultiSelect<
	TProps extends IMultiSelectProps = IMultiSelectProps,
>({
	items,
	onChange,
	initialSelectedItems,
	itemToKey,
	itemToString,
	placeholder,
	ariaLabel,
	disabled,
	invalid,
	className,
	icon,
	placeholderAsLabel,
	onClick,
	...rest
}: TProps) {
	const [isOpen, setIsOpen] = useState(false);

	const getItemKey = itemToKey || getItemKeyDefault;
	const getItemDisplayValue = itemToString || getItemDisplayValueDefault;

	const handleStateChange = (changes: StateChangeOptions<IDropdownItem>) => {
		if (changes.type === Downshift.stateChangeTypes.keyDownEscape) {
			setIsOpen(false);
		} else if (
			changes.type === Downshift.stateChangeTypes.keyDownArrowDown ||
			changes.type === Downshift.stateChangeTypes.keyDownArrowUp
		) {
			setIsOpen(true);
		}
	};

	return (
		<Downshift
			itemToString={getItemDisplayValue}
			isOpen={isOpen}
			onStateChange={handleStateChange}
			selectedItem={null}
			onOuterClick={() => setIsOpen(false)}
			id="downshift-multi-select"
			{...rest}
		>
			{({ getRootProps, ...props }) => (
				<MultiSelectRoot
					{...getRootProps({ refKey: 'innerRef' })}
					getItemDisplayValue={
						rest.dropdownItemValue
							? rest.dropdownItemValue
							: getItemDisplayValue
					}
					getItemKey={getItemKey}
					items={items}
					disabled={disabled}
					invalid={invalid}
					className={className}
					ariaLabel={ariaLabel ?? placeholder}
					placeholder={placeholder}
					icon={icon}
					placeholderAsLabel={placeholderAsLabel}
					onClick={onClick}
					initialSelectedItems={initialSelectedItems}
					setIsOpen={setIsOpen}
					onChange={onChange}
					{...props}
					isOpen={isOpen}
				/>
			)}
		</Downshift>
	);
}
